import { ClipboardList } from 'lucide-react-native';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { SharedSurvey } from '@/services/conversations/sharedSurvey';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

type SharedSurveyCardProps = {
  survey: SharedSurvey;
  /** Opens the survey; the card shows no button without it, as while the message is sending. */
  onOpen?: () => void;
};

/**
 * The survey a support message shares, inside the message. Everything it shows
 * rides on the message: the API snapshots the survey's name when it is sent,
 * so the card makes no request and renders complete over realtime. It never
 * says whether the member has answered: that would need a read per card, and
 * the survey screen already tells them when they open it.
 *
 * It opens the survey group rather than a version, so a survey republished
 * after the message was sent opens on its current version.
 *
 * Both sides see the same "Take survey": the card is the member's invitation,
 * and staff who shared it should read it as the member does.
 */
export function SharedSurveyCard({ survey, onOpen }: SharedSurveyCardProps) {
  return (
    <VStack className="mt-3 gap-2 rounded-xl border border-outline-200 bg-background-50 p-3">
      <HStack className="items-center gap-1.5">
        <Icon as={ClipboardList} className="h-3.5 w-3.5 text-typography-700" aria-hidden />
        <Text className="font-semibold text-xs text-typography-700">Survey</Text>
      </HStack>

      <Text className="font-semibold text-sm text-typography-900" numberOfLines={3}>
        {survey.name}
      </Text>

      {onOpen ? (
        <VStack className="items-start">
          <Button
            size="sm"
            className={TOUCH_TARGET_MIN_H}
            onPress={onOpen}
            accessibilityLabel={`Take survey: ${survey.name}`}>
            <ButtonText>Take survey</ButtonText>
          </Button>
        </VStack>
      ) : null}
    </VStack>
  );
}
